// ============================================================================
// Claude Code WebSocket Client
//
// Connects to the claude-agent-server at ws://host:port/ws
// Much simpler than OpenClaw — no RPC protocol, no challenge-nonce auth.
// Just plain JSON messages over WebSocket.
//
// Usage:
//   const client = new ClaudeCodeClient({ url: "ws://localhost:3000", apiKey: "sk-ant-...", onMessage, onStatusChange });
//   await client.connect();
//   client.sendMessage("Hello!", "session-1");
//   client.interrupt();
//   client.disconnect();
// ============================================================================
// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------
export class ClaudeCodeClient {
    ws = null;
    config;
    status = "disconnected";
    reconnectAttempt = 0;
    reconnectTimer = null;
    disposed = false;
    constructor(config) {
        this.config = { autoReconnect: true, ...config };
    }
    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------
    /** Connect to the claude-agent-server. Sends config first, then opens WebSocket. */
    async connect() {
        if (this.disposed)
            return;
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        this.setStatus("connecting");
        // 1. Send config via HTTP POST before connecting WebSocket
        try {
            await this.sendConfig();
        }
        catch (err) {
            console.error("[ClaudeCode] Failed to send config:", err);
            // Continue anyway — config might already be set
        }
        // 2. Open WebSocket
        const wsUrl = this.config.url.replace(/^http/, "ws").replace(/\/$/, "") + "/ws";
        console.log("[ClaudeCode] >>> Connecting to:", wsUrl);
        try {
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = this.handleOpen;
            this.ws.onmessage = this.handleMessage;
            this.ws.onclose = this.handleClose;
            this.ws.onerror = this.handleError;
        }
        catch (err) {
            console.error("[ClaudeCode] WebSocket construction failed:", err);
            this.setStatus("error");
            this.scheduleReconnect();
        }
    }
    /** Gracefully close the connection */
    disconnect() {
        this.disposed = true;
        this.clearReconnectTimer();
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close();
            this.ws = null;
        }
        this.setStatus("disconnected");
    }
    /** Send a user message to Claude */
    sendMessage(text, sessionId) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.error("[ClaudeCode] Cannot send: not connected");
            return;
        }
        const frame = JSON.stringify({
            type: "user_message",
            data: {
                type: "user",
                session_id: sessionId,
                message: {
                    role: "user",
                    content: text
                }
            }
        });
        console.log("[ClaudeCode] >>> SENDING:", frame.slice(0, 200));
        this.ws.send(frame);
    }
    /** Interrupt the current agent operation */
    interrupt() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            return;
        const frame = JSON.stringify({ type: "interrupt" });
        console.log("[ClaudeCode] >>> SENDING interrupt");
        this.ws.send(frame);
    }
    /** Check if connected */
    get isConnected() {
        return this.status === "connected";
    }
    /** Get current status */
    get connectionStatus() {
        return this.status;
    }
    // -------------------------------------------------------------------------
    // Config — POST /config before connecting
    // -------------------------------------------------------------------------
    async sendConfig() {
        const httpUrl = this.config.url.replace(/^ws/, "http").replace(/\/$/, "");
        const configPayload = {
            model: this.config.model
        };
        // Only send apiKey if provided (otherwise server uses its own)
        if (this.config.apiKey) {
            configPayload.anthropicApiKey = this.config.apiKey;
        }
        console.log("[ClaudeCode] >>> Sending config to:", `${httpUrl}/config`, configPayload);
        const res = await fetch(`${httpUrl}/config`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(configPayload)
        });
        if (!res.ok) {
            throw new Error(`Config failed: ${res.status} ${res.statusText}`);
        }
        const data = await res.json();
        console.log("[ClaudeCode] Config set:", data);
    }
    // -------------------------------------------------------------------------
    // WebSocket event handlers
    // -------------------------------------------------------------------------
    handleOpen = () => {
        console.log("[ClaudeCode] WebSocket opened");
        this.reconnectAttempt = 0;
        // Don't set connected yet — wait for the { type: "connected" } message
    };
    handleMessage = (evt) => {
        let message;
        try {
            message = JSON.parse(evt.data);
        }
        catch {
            console.warn("[ClaudeCode] Failed to parse message:", evt.data);
            return;
        }
        console.log("[ClaudeCode] <<< RECEIVED:", message.type, JSON.stringify(evt.data).slice(0, 300));
        // Handle connection confirmation
        if (message.type === "connected") {
            this.setStatus("connected");
        }
        // Forward all messages to the subscriber
        this.config.onMessage?.(message);
    };
    handleClose = (evt) => {
        console.warn("[ClaudeCode] WebSocket closed:", evt.code, evt.reason);
        this.ws = null;
        if (!this.disposed && this.config.autoReconnect) {
            this.setStatus("reconnecting");
            this.scheduleReconnect();
        }
        else {
            this.setStatus("disconnected");
        }
    };
    handleError = (_evt) => {
        console.error("[ClaudeCode] WebSocket error");
    };
    // -------------------------------------------------------------------------
    // Reconnect logic (exponential backoff: 800ms → 15s)
    // -------------------------------------------------------------------------
    scheduleReconnect() {
        this.clearReconnectTimer();
        const delay = Math.min(800 * Math.pow(2, this.reconnectAttempt), 15_000);
        this.reconnectAttempt++;
        console.log(`[ClaudeCode] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);
        this.reconnectTimer = setTimeout(() => this.connect(), delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------
    setStatus(status) {
        if (this.status === status)
            return;
        this.status = status;
        this.config.onStatusChange?.(status);
    }
}
